import { GetStaticPaths, GetStaticProps } from 'next';
import Image from 'next/image';
import { BlogJsonLd } from 'next-seo';

import Layout from '@/components/Layout/Vanilla';
import { formatDate } from '@/utils/date';
import { fetchAPI, gql, sdk } from '@/lib/graphcms';
import { Post } from '@/types';
import { SliderProvider } from '@/components/Slider/context';
import Breadcrump, { BreadcrumpPages } from '@/components/blog/breadcrump';
import Badge from '@/components/blog/badge';
import Authors from '@/components/blog/authors';
import Card from '@/components/blog/card';
import Heading from '@/components/Heading';
import AuthorsSlider, { Author } from '@/components/blog/authors-slider';
import Content from '@/components/blog/content';
import { useScrollToAnchor } from '@/hooks/useScrollToAnchor';

type BlogPageProps = {
  slug: string;
  preview: boolean;
  blogPost: Post;
  relatedContent: {
    id: string;
    slug: string;
    title: string;
    subTitle: string;
    coverImage: {
      url: string;
      width: number;
      height: number;
      alt: string;
    };
    authors: Author[];
    categories: {
      id: string;
      slug: string;
      title: string;
    }[];
    publishedOn: string;
  }[];
};

export default function BlogPage({
  relatedContent,
  blogPost,
  slug,
  preview,
}: BlogPageProps) {
  useScrollToAnchor();

  const friendlyPublishedOn = formatDate(blogPost?.publishedOn);
  const primaryCategory = blogPost.categories[0];

  const breadcrumpPages: BreadcrumpPages = [
    {
      name: `Blog`,
      href: `/blog`,
    },
  ];

  if (primaryCategory?.title) {
    breadcrumpPages.push({
      name: primaryCategory?.title,
      href: `/blog/tag/${primaryCategory?.slug}`,
    });
  }

  if (blogPost?.title) {
    breadcrumpPages.push({
      name: blogPost?.title,
      href: `/blog/${slug}`,
      current: true,
    });
  }

  return (
    <Layout seo={blogPost} preview={preview}>
      <BlogJsonLd
        url={`https://graphcms.com/blog/${slug}`}
        title={blogPost?.title}
        images={[blogPost?.coverImage?.url]}
        datePublished={new Date(blogPost?.publishedOn).toISOString()}
        dateModified={blogPost?.updatedAt || ``}
        authorName={blogPost?.authors.map((author) => author.name)}
        description={blogPost?.subTitle}
      />

      <div className="max-w-6xl mx-auto px-6">
        <Breadcrump pages={breadcrumpPages} />
      </div>

      <div className="pt-8 overflow-hidden sm:pt-12 lg:relative lg:py-24 flex flex-col lg:block">
        <div className="sm:mx-auto max-w-lg px-6 sm:max-w-3xl lg:max-w-6xl lg:grid lg:grid-cols-2 lg:gap-24 order-2 lg:order-1">
          <div>
            <div className="mb-4 space-x-1">
              {blogPost.categories.length > 0 &&
                blogPost.categories.map((category, index) => {
                  // If there are more than two categories, we show just the first ones
                  if (index > 1) return null;

                  return (
                    <Badge
                      key={category.slug}
                      slug={`/blog/tag/${category.slug}`}
                      title={category.title}
                      variant="primary"
                    />
                  );
                })}
            </div>
            <div className="space-y-4">
              <Heading className="font-bold text-haiti">
                {blogPost.title}
              </Heading>
              <p className="text-zodiac leading-relaxed text-xl md:text-[22px]">
                {blogPost.subTitle}
              </p>
            </div>
            <div className="mt-6 flex justify-between items-center">
              {blogPost.authors.length > 0 && (
                <Authors
                  authors={blogPost.authors}
                  type="large"
                  showAuthorsName
                />
              )}

              <div className="h-full text-xs text-zodiac uppercase ml-3">
                <time
                  dateTime={blogPost.publishedOn}
                  className="h-full flex items-center pt-[1px]"
                >
                  {friendlyPublishedOn}
                </time>
              </div>
            </div>
          </div>
        </div>

        <div className="sm:mx-auto sm:max-w-3xl sm:px-6 order-1 lg:order-2 mb-6 lg:mb-0">
          <div className="sm:relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 flex">
            <div className="relative pl-4 pr-4 lg:pr-0 lg:-mr-40 sm:max-w-3xl sm:px-0 lg:max-w-none lg:pl-12 rounded flex my-auto mx-0">
              {blogPost?.coverImage?.url && (
                <Image
                  width={750}
                  height={422}
                  src={blogPost.coverImage.url}
                  alt={blogPost.coverImage.alt}
                  className="rounded ring-1 ring-black ring-opacity-5"
                  objectFit="cover"
                  quality={100}
                  priority
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 mt-10 md:mt-16 relative">
        <Content content={blogPost?.content || ``} postTitle={blogPost.title} />
      </div>

      <div className="max-w-6xl mx-auto px-6 my-10 lg:mt-24 lg:mb-20">
        <hr className="border-t border-gray-200" />

        {blogPost.authors.length > 0 && (
          <div className="max-w-3xl mx-auto mt-12">
            <SliderProvider items={blogPost.authors}>
              <AuthorsSlider />
            </SliderProvider>
          </div>
        )}

        {relatedContent.length > 0 && (
          <>
            <h3 className="font-bold text-3xl mt-12 lg:mt-20">
              Related articles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 mt-6 md:mt-10">
              {relatedContent.map((post) => (
                <Card post={post} key={post.id} />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async ({
  preview = false,
  params,
}) => {
  const slug = Array.isArray(params?.slug) ? params?.slug[0] : params?.slug;

  const { blogPost } = await sdk({ preview }).blogContentPage({
    slug: slug || ``,
  });

  if (!blogPost) {
    return {
      notFound: true,
    };
  }

  const { relatedContent } =
    (await fetchAPI<Pick<BlogPageProps, 'relatedContent'>>(
      gql`
        query ($slug: String!, $relatedValues: [String!]) {
          relatedContent: blogPosts(
            where: {
              slug_not: $slug
              categories_some: { slug_in: $relatedValues }
            }
            first: 3
            orderBy: publishedOn_DESC
          ) {
            id
            slug
            title
            subTitle
            coverImage {
              url(
                transformation: { image: { resize: { width: 960, fit: clip } } }
              )
              width
              height
              alt
            }
            authors {
              name
              title
              twitterHandle
              photo {
                url
                alt
                width
                height
              }
            }
            categories {
              id
              slug
              title
            }
            publishedOn
          }
        }
      `,
      {
        preview,
        variables: {
          slug,
          relatedValues: blogPost.categories.map(
            ({ slug: categorySlug }) => categorySlug,
          ),
        },
      },
    )) || [];

  return {
    props: {
      preview,
      blogPost,
      relatedContent,
      slug,
    },
    revalidate: 240,
  };
};

export const getStaticPaths: GetStaticPaths = async () =>
  Promise.resolve({
    paths: [],
    fallback: `blocking`,
  });
